import type { CompanyInput, UnitInput } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { COMPANY_QUERY_KEYS } from '../constants';
import { companyService } from '../services';
import type { CompanyHourDay, MonthlyGoalInput } from '../types';

export function useCompany() {
  return useQuery({
    queryKey: COMPANY_QUERY_KEYS.me,
    queryFn: () => companyService.me(),
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: Partial<CompanyInput>) => companyService.update(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: COMPANY_QUERY_KEYS.me }),
  });
}

export function useGoals() {
  return useQuery({
    queryKey: COMPANY_QUERY_KEYS.goals,
    queryFn: () => companyService.goals(),
  });
}

export function useSaveGoals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: MonthlyGoalInput) => companyService.saveGoals(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company'] }),
  });
}

export function useUnits() {
  return useQuery({
    queryKey: COMPANY_QUERY_KEYS.units,
    queryFn: () => companyService.listUnits(),
  });
}

function useUnitMutation<TArgs>(mutationFn: (args: TArgs) => Promise<unknown>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: COMPANY_QUERY_KEYS.units }),
        queryClient.invalidateQueries({ queryKey: COMPANY_QUERY_KEYS.me }),
      ]),
  });
}

export function useCreateUnit() {
  return useUnitMutation((input: UnitInput) => companyService.createUnit(input));
}

export function useUpdateUnit() {
  return useUnitMutation(({ id, input }: { id: string; input: Partial<UnitInput> }) =>
    companyService.updateUnit(id, input),
  );
}

export function useConnectWhatsapp() {
  return useUnitMutation(({ id, phone }: { id: string; phone: string }) =>
    companyService.connectWhatsapp(id, phone),
  );
}

export function useLinkWhatsappAccount() {
  return useUnitMutation(
    ({ id, ...input }: { id: string; accountSid: string; authToken: string; phone: string }) =>
      companyService.linkWhatsappAccount(id, input),
  );
}

export function useRefreshWhatsapp() {
  return useUnitMutation((id: string) => companyService.refreshWhatsapp(id));
}

export function useDeleteUnit() {
  return useUnitMutation((id: string) => companyService.removeUnit(id));
}

export function useCompanyHours() {
  return useQuery({
    queryKey: COMPANY_QUERY_KEYS.hours,
    queryFn: () => companyService.hours(),
  });
}

export function useSaveCompanyHours() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (days: CompanyHourDay[]) => companyService.saveHours(days),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: COMPANY_QUERY_KEYS.hours }),
  });
}
